import { Link } from "react-router-dom";
import { ArrowRight, Clock, Moon, TrendingUp, Euro } from "lucide-react";
import { Logo } from "../components/AppLayout";

const HERO = "https://images.unsplash.com/photo-1790986119054-6465d40c2584?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const WORKERS = [
  { img: "https://images.pexels.com/photos/6129151/pexels-photo-6129151.jpeg?auto=compress&cs=tinysrgb&w=800", label: "Nurses" },
  { img: "https://images.pexels.com/photos/31570314/pexels-photo-31570314.jpeg?auto=compress&cs=tinysrgb&w=800", label: "Warehouse crews" },
  { img: "https://images.pexels.com/photos/8942691/pexels-photo-8942691.jpeg?auto=compress&cs=tinysrgb&w=800", label: "Security & response" },
];
const FEATURES = [
  { icon: Clock, title: "Start, end, done", text: "Breaks are taken off automatically based on your rule." },
  { icon: Moon, title: "Night shifts handled", text: "22:00 to 06:00? Counted as one 8-hour shift." },
  { icon: TrendingUp, title: "Overtime tracked", text: "Daily and weekly limits, paid at your overtime rate." },
  { icon: Euro, title: "Monthly pay estimate", text: "See your gross salary build up shift by shift." },
];

export default function Landing() {
  return (
    <div className="grain min-h-screen bg-background">
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
        <Logo />
        <Link to="/sign-in" data-testid="landing-signin-link" className="text-sm font-semibold text-muted-foreground transition-colors hover:text-foreground">Sign in</Link>
      </header>

      <section className="relative overflow-hidden">
        <img src={HERO} alt="Bus driver on a night shift" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F17] via-[#0B0F17]/85 to-[#0B0F17]/30" />
        <div className="relative z-10 mx-auto max-w-7xl px-5 pb-24 pt-16 sm:px-8 sm:pt-24 lg:pb-32">
          <span className="rise inline-flex items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 font-num text-xs font-bold uppercase tracking-wider text-amber-300">
            <span className="h-2 w-2 animate-pulse rounded-full bg-amber-500" /> For shift workers
          </span>
          <h1 className="rise mt-6 max-w-3xl font-heading text-5xl font-black uppercase leading-[0.92] tracking-tight sm:text-6xl lg:text-7xl" style={{ animationDelay: "80ms" }}>
            Know your hours.<br /><span className="text-amber-500">Know your pay.</span>
          </h1>
          <p className="rise mt-6 max-w-xl text-base text-muted-foreground sm:text-lg" style={{ animationDelay: "160ms" }}>
            ShiftMate tracks every shift, takes off your breaks, counts your overtime and estimates your monthly gross salary — no spreadsheets, no guesswork.
          </p>
          <div className="rise mt-10 flex flex-wrap items-center gap-4" style={{ animationDelay: "240ms" }}>
            <Link to="/sign-in" data-testid="landing-get-started-btn"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 font-bold text-[#0B0F17] transition-transform duration-150 hover:-translate-y-0.5 hover:bg-amber-400 active:scale-[0.98]">
              Start tracking free <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="text-sm text-muted-foreground">Sign in with Google · free forever</span>
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <h2 className="font-heading text-4xl font-extrabold uppercase tracking-tight">Built for the people<br />who keep things running</h2>
            <p className="mt-4 text-muted-foreground">Bus drivers, nurses, warehouse workers and security guards — anyone whose hours change week to week.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:col-span-8">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div key={title} className="rounded-2xl border border-border/70 bg-card p-6 transition-colors duration-200 hover:border-amber-500/40">
                <Icon className="h-5 w-5 text-amber-500" />
                <h3 className="mt-4 text-lg font-bold">{title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-16 grid gap-4 sm:grid-cols-3">
          {WORKERS.map((w) => (
            <div key={w.label} className="relative h-64 overflow-hidden rounded-2xl border border-border/70">
              <img src={w.img} alt={w.label} className="h-full w-full object-cover transition-transform duration-500 hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] to-transparent" />
              <span className="absolute bottom-4 left-4 font-heading text-2xl font-extrabold uppercase">{w.label}</span>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 border-t border-border py-8 text-center text-xs text-muted-foreground">
        ShiftMate · Salary figures are gross estimates, before tax and deductions.
      </footer>
    </div>
  );
}
